import Flex from "../../components/Flex";
import Text from "../../components/Text";
import CustomTitle from "../../components/app/CustomTitle";
import {
  GROUP_SESSION_PRINT_LABEL,
  GROUP_SESSION_SIGNED_LINE,
  SESSION_INFO_EMPTY_VALUE,
  SESSION_NOTE_FIELD_LABEL,
  SESSION_NOTE_LABEL,
} from "../../pages/app/provider/group-visit/group-visit-constant";
import { GroupSessionPrintData } from "../../pages/app/provider/group-visit/group-visit-types";
import {
  KeyValPair,
  ListContent,
  RenderTextWithContainerPattern,
  ReportContainer,
  ReportSection,
} from "../constant";

export interface GroupSessionNoteTemplateProps {
  data: GroupSessionPrintData;
}

const CELL_STYLE = {
  border: "1px solid #000",
  padding: "4px 6px",
  fontSize: "14px",
  textAlign: "left" as const,
};

const PRINT_STYLES = `
  .group-session-pdf-root {
    padding-top: 12px;
    padding-bottom: 12px;
    orphans: 4;
    widows: 4;
  }
  .group-session-pdf-root p,
  .group-session-pdf-root li,
  .group-session-pdf-root tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .group-session-pdf-root p[style*="background"] {
    page-break-after: avoid;
    break-after: avoid;
  }
`;

/**
 * The shared group note as a printable report: the session's therapy details,
 * every patient who participated, and the one note that covers them all.
 *
 * @author Rajkumar Rathod
 */
const GroupSessionNoteTemplate = ({ data }: GroupSessionNoteTemplateProps) => (
  <div
    className="group-session-pdf-root"
    style={{ display: "flex", flexDirection: "column", gap: "10px" }}
  >
    <style>{PRINT_STYLES}</style>

    <Flex justifyContent={"center"} alignItems={"center"} mb={2}>
      <CustomTitle>{GROUP_SESSION_PRINT_LABEL.title}</CustomTitle>
    </Flex>

    <ReportContainer label={GROUP_SESSION_PRINT_LABEL.sessionDetails}>
      <div style={{ display: "flex", gap: "10px", padding: "2px" }}>
        <div style={{ flex: 1 }}>
          <KeyValPair
            k={GROUP_SESSION_PRINT_LABEL.groupName}
            v={data.groupName}
          />
          <KeyValPair
            k={GROUP_SESSION_PRINT_LABEL.groupType}
            v={data.groupType}
          />
          <KeyValPair
            k={GROUP_SESSION_PRINT_LABEL.therapyCategory}
            v={data.therapyCategory}
          />
          <KeyValPair
            k={GROUP_SESSION_PRINT_LABEL.sessionInfo}
            v={data.sessionInfo}
          />
        </div>
        <div style={{ flex: 1 }}>
          <KeyValPair
            k={GROUP_SESSION_PRINT_LABEL.dateTime}
            v={data.dateTime}
          />
          <KeyValPair k={GROUP_SESSION_PRINT_LABEL.mode} v={data.mode} />
          <KeyValPair k={GROUP_SESSION_PRINT_LABEL.status} v={data.status} />
        </div>
        <div style={{ flex: 1 }}>
          <KeyValPair
            k={GROUP_SESSION_PRINT_LABEL.therapists}
            v={data.therapists}
          />
          <KeyValPair
            k={GROUP_SESSION_PRINT_LABEL.attendees}
            v={`${data.attendeeCount}`}
          />
        </div>
      </div>
    </ReportContainer>

    <ReportContainer label={GROUP_SESSION_PRINT_LABEL.participants} flow>
      <div style={{ padding: "4px" }}>
        {data.participants.length ? (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th style={CELL_STYLE}>
                  {GROUP_SESSION_PRINT_LABEL.serialNumber}
                </th>
                <th style={CELL_STYLE}>
                  {GROUP_SESSION_PRINT_LABEL.patientName}
                </th>
                {/* <th style={CELL_STYLE}>{GROUP_SESSION_PRINT_LABEL.mrn}</th> */}
                <th style={CELL_STYLE}>
                  {GROUP_SESSION_PRINT_LABEL.birthDate}
                </th>
                <th style={CELL_STYLE}>{GROUP_SESSION_PRINT_LABEL.email}</th>
              </tr>
            </thead>
            <tbody>
              {data.participants.map((participant, index) => (
                <tr key={`${participant.name}-${index}`}>
                  <td style={CELL_STYLE}>{index + 1}</td>
                  <td style={CELL_STYLE}>{participant.name}</td>
                  {/* <td style={CELL_STYLE}>{participant.mrn}</td> */}
                  <td style={CELL_STYLE}>{participant.birthDate}</td>
                  <td style={CELL_STYLE}>{participant.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ margin: "5px 0", fontSize: "14px" }}>
            {GROUP_SESSION_PRINT_LABEL.noParticipants}
          </p>
        )}
      </div>
    </ReportContainer>

    <RenderTextWithContainerPattern
      label={SESSION_NOTE_FIELD_LABEL.sessionObjectives}
      val={data.sessionObjectives}
    />

    <ReportSection label={GROUP_SESSION_PRINT_LABEL.clinicalDocumentation}>
      <RenderTextWithContainerPattern
        label={SESSION_NOTE_FIELD_LABEL.topicsCovered}
        val={data.topicsCovered}
      />

      {(!!data.therapeuticInterventions.length ||
        !!data.therapeuticInterventionsNote?.trim()) && (
        <ReportContainer
          label={SESSION_NOTE_FIELD_LABEL.therapeuticInterventions}
          flow
        >
          <ListContent list={data.therapeuticInterventions} />
          {!!data.therapeuticInterventionsNote?.trim() && (
            <KeyValPair
              k={SESSION_NOTE_LABEL.addNote}
              v={data.therapeuticInterventionsNote}
            />
          )}
        </ReportContainer>
      )}

      {!!data.groupActivities.length && (
        <ReportContainer label={SESSION_NOTE_FIELD_LABEL.groupActivities} flow>
          <ListContent list={data.groupActivities} />
        </ReportContainer>
      )}

      <div style={{ padding: "2px" }}>
        <KeyValPair
          k={SESSION_NOTE_FIELD_LABEL.homeworkAssigned}
          v={data.homeworkAssigned}
        />
        {data.isHomeworkAssigned && (
          <KeyValPair
            k={SESSION_NOTE_FIELD_LABEL.homework}
            v={data.homework || SESSION_INFO_EMPTY_VALUE}
          />
        )}
      </div>

      <RenderTextWithContainerPattern
        label={SESSION_NOTE_FIELD_LABEL.overallGroupSummary}
        val={data.overallGroupSummary}
      />
    </ReportSection>

    {data.isSigned && (
      <ReportContainer label={GROUP_SESSION_PRINT_LABEL.signedBy}>
        <div
          style={{
            pageBreakInside: "avoid",
            breakInside: "avoid",
            padding: "2px",
          }}
        >
          <Text>
            {`${GROUP_SESSION_SIGNED_LINE.prefix} ${data.signedBy} ${GROUP_SESSION_SIGNED_LINE.on} ${data.signedOnDate} ${GROUP_SESSION_SIGNED_LINE.at} ${data.signedOnTime}`}
          </Text>
          {!!data.providerSignature && (
            <img
              src={data.providerSignature}
              alt="signature"
              width={"120px"}
              height={"120px"}
              style={{
                display: "block",
                objectFit: "contain",
                pageBreakInside: "avoid",
                breakInside: "avoid",
              }}
            />
          )}
        </div>
      </ReportContainer>
    )}
  </div>
);

export default GroupSessionNoteTemplate;
